import * as React from "react"
import { router } from "@inertiajs/react"
import { Check, MoreHorizontal, Pencil, Trash2, X } from "lucide-react"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { RichTextField } from "@/components/ui/rich-text-field"
import type { CurrentUser } from "@/types/inertia"
import type { BaseActivityEvent } from "./types"

/**
 * One event row in any activity feed.
 *
 * Domain-agnostic layout — the per-domain bits (status badges, voice-compass
 * slot rendering, etc.) come in via `renderSummary`.
 *
 *   <summary>  ──── connector ────  <timestamp>  <kebab>
 *   ┌──────────────────────────────────────────────────┐
 *   │  <body content>                                  │
 *   └──────────────────────────────────────────────────┘
 */
export function ActivityEventCard<Event extends BaseActivityEvent>({
  event,
  eventsPath,
  currentUser,
  renderSummary,
}: {
  event: Event
  eventsPath: string
  currentUser: CurrentUser
  renderSummary: (event: Event) => React.ReactNode
}) {
  const canEdit =
    event.author_type === "user" &&
    currentUser != null &&
    event.author_identifier === String(currentUser.id)

  const [editing, setEditing] = React.useState(false)

  // The kebab menu only renders when the event is editable. Once the user is
  // mid-edit, the menu hides (the editor has its own Cancel/Save).
  const showMenu = canEdit && event.body && !editing
  const isStandaloneComment = event.event_type === "commented"
  const menu = showMenu ? (
    <EventActionsMenu
      onEdit={() => setEditing(true)}
      onDelete={() => deleteEvent(eventsPath, event.id, isStandaloneComment)}
      deleteLabel={isStandaloneComment ? "Delete" : "Clear comment"}
    />
  ) : null

  return (
    <div>
      <div className="flex items-center gap-3 text-sm text-ink-body">
        <span className="shrink-0">{renderSummary(event)}</span>
        <span className="h-px flex-1 bg-hairline" aria-hidden="true" />
        <span className="flex shrink-0 items-center gap-1.5">
          <Timestamp iso={event.created_at} />
          {menu}
        </span>
      </div>

      {event.body && (
        <div className="mt-2 rounded-md border border-hairline bg-surface px-3 py-2">
          {editing ? (
            <BodyEditor
              event={event}
              eventsPath={eventsPath}
              onDone={() => setEditing(false)}
              standalone={isStandaloneComment}
            />
          ) : (
            <BodyRendered body={event.body} />
          )}
        </div>
      )}
    </div>
  )
}

function Timestamp({ iso }: { iso: string }) {
  const date = new Date(iso)
  return (
    <time dateTime={iso} className="text-[12px] text-ink-muted">
      {date.toLocaleString()}
    </time>
  )
}

function BodyRendered({ body }: { body: string }) {
  return (
    <div className="text-sm text-ink-body">
      <RichTextField defaultValue={body} readOnly />
    </div>
  )
}

function BodyEditor({
  event,
  eventsPath,
  onDone,
  standalone,
}: {
  event: BaseActivityEvent
  eventsPath: string
  onDone: () => void
  standalone: boolean
}) {
  const [draft, setDraft] = React.useState(event.body ?? "")
  const [saving, setSaving] = React.useState(false)

  const save = () => {
    const next = draft.trim()
    if (standalone && next.length === 0) {
      // Empty body on a standalone comment = delete.
      deleteEvent(eventsPath, event.id, true)
      return
    }
    setSaving(true)
    router.patch(
      `${eventsPath}/${event.id}`,
      { body: next },
      {
        preserveScroll: true,
        onFinish: () => {
          setSaving(false)
          onDone()
        },
      },
    )
  }

  return (
    <div>
      <RichTextField defaultValue={event.body ?? ""} onChange={setDraft} />
      <div className="mt-2 flex items-center justify-end gap-2">
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onDone}>
          <X size={14} /> Cancel
        </Button>
        <Button type="button" size="sm" variant="primary" disabled={saving} onClick={save}>
          <Check size={14} /> {saving ? "Saving…" : "Save"}
        </Button>
      </div>
    </div>
  )
}

function EventActionsMenu({
  onEdit,
  onDelete,
  deleteLabel,
}: {
  onEdit: () => void
  onDelete: () => void
  deleteLabel: string
}) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label="Event actions"
        className="-mr-1 inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-md text-ink-muted hover:bg-page hover:text-ink-display focus:outline-none focus:ring-2 focus:ring-accent"
      >
        <MoreHorizontal size={14} />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[9rem]">
        <DropdownMenuItem onSelect={onEdit}>
          <Pencil />
          <span>Edit</span>
        </DropdownMenuItem>
        <DropdownMenuItem destructive onSelect={onDelete}>
          <Trash2 />
          <span>{deleteLabel}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function deleteEvent(eventsPath: string, eventId: number, confirmFirst: boolean) {
  if (confirmFirst && !window.confirm("Delete this comment?")) return
  router.delete(`${eventsPath}/${eventId}`, { preserveScroll: true })
}
